import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BoxIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, CircleDollarSignIcon, FilterIcon, SearchIcon, XIcon } from 'lucide-react';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { NisoloProductTable } from './NisoloProductTable';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
type UnitStatus = 'Active' | 'Sold' | 'Inactive';
type Channel = 'Manymoons' | 'Whatnot' | 'Collective' | 'Circular Shop';
type UnitRow = {
  id: number;
  name: string;
  size: string;
  condition: 'Rescue' | 'Resale' | 'Damaged';
  status: UnitStatus;
  received: number;
  sold: number;
  dateLive: string;
  channel?: Channel;
  msrp: number;
  currentPrice: number;
  imageUrl: string;
};
const productSeeds = [{
  name: 'Caroline Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg',
  msrp: 398
}, {
  name: 'Sydney Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/16fa4f2a-8577-49a4-901a-e63a54451ebd.jpg',
  msrp: 498
}, {
  name: 'Sophia Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/650d6552-e453-4201-8166-9316b1528a58.jpg',
  msrp: 548
}, {
  name: 'Katherine Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg',
  msrp: 598
}, {
  name: 'Sarah Bird Mini Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/16fa4f2a-8577-49a4-901a-e63a54451ebd.jpg',
  msrp: 498
}, {
  name: 'Daisy Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/650d6552-e453-4201-8166-9316b1528a58.jpg',
  msrp: 568
}, {
  name: 'Desirae Midi Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/870c32c0-b80e-4da5-9260-ac098cc1a3bb.jpg',
  msrp: 578
}];
const sizes = ['X-Small', 'Small', 'Medium', 'Large', 'X-Large'];
const channels: Channel[] = ['Manymoons', 'Whatnot', 'Collective', 'Circular Shop'];
const PAGE_SIZE = 12;
function createMockUnits() {
  return Array.from({
    length: 150
  }, (_, index): UnitRow => {
    const product = productSeeds[index % productSeeds.length];
    const received = 1 + index % 7;
    const sold = index % 5 === 0 ? received : index % 3 === 0 ? 1 : 0;
    const status: UnitStatus = sold === received ? 'Sold' : index % 13 === 0 ? 'Inactive' : 'Active';
    const condition = index % 19 === 0 ? 'Damaged' : index % 11 === 0 ? 'Resale' : 'Rescue';
    return {
      id: index + 1,
      name: product.name,
      size: sizes[index % sizes.length],
      condition,
      status,
      received,
      sold,
      dateLive: index % 4 === 0 ? 'Jun 30, 2026' : index % 3 === 0 ? 'Jun 24, 2026' : 'Dec 18, 2025',
      channel: sold > 0 ? channels[index % channels.length] : undefined,
      msrp: product.msrp,
      currentPrice: Math.round(product.msrp * (condition === 'Rescue' ? 0.28 : condition === 'Resale' ? 0.45 : 0.1)),
      imageUrl: product.imageUrl
    };
  });
}
const unitRows = createMockUnits();
export function UnitsDashboard({
  selectedOwner,
  embedded = false
}: {
  selectedOwner: string;
  embedded?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UnitStatus | 'All'>('All');
  const [page, setPage] = useState(1);
  const [selectedUnit, setSelectedUnit] = useState<UnitRow | null>(null);
  const isNisolo = selectedOwner === 'Nisolo';
  const {
    inventory: nisolo,
    loading: nisoloLoading,
    error: nisoloError
  } = useNisoloProducts(isNisolo);
  const {
    data: nisoloSubmissions
  } = useNisoloSubmissions(isNisolo);
  const {
    data: nisoloOrders
  } = useNisoloOrders(isNisolo);
  const hasData = selectedOwner === 'Eddy' || isNisolo && Boolean(nisolo);
  const nisoloRows = useMemo<UnitRow[]>(() => {
    if (!nisolo) return [];
    return nisolo.products.flatMap((product, productIndex) => (product.sizes.length > 0 ? product.sizes : ['One size']).map((size, sizeIndex): UnitRow => {
      const perSize = Math.max(1, Math.round(product.units / Math.max(1, product.sizes.length || 1)));
      const unitMsrp = product.variants > 0 ? Math.round(product.msrp / Math.max(1, product.units || 1)) : 0;
      return {
        id: productIndex * 100 + sizeIndex + 1,
        name: product.title,
        size,
        condition: 'Rescue',
        status: product.status.toLocaleLowerCase() === 'active' ? 'Active' : 'Inactive',
        received: perSize,
        sold: 0,
        dateLive: 'Feb 15, 2026',
        msrp: unitMsrp || product.price,
        currentPrice: product.price,
        imageUrl: product.image
      };
    }));
  }, [nisolo]);
  const sourceRows = isNisolo ? nisoloRows : unitRows;
  const summary = isNisolo ? {
    received: (nisoloSubmissions?.totalReceived ?? 0).toLocaleString('en-US'),
    receivedDetail: `Across ${(nisolo?.totalProducts ?? 0).toLocaleString('en-US')} products`,
    sold: (nisoloOrders?.units ?? 0).toLocaleString('en-US'),
    sellThrough: nisoloSubmissions && nisoloSubmissions.totalReceived > 0 ? `${((nisoloOrders?.units ?? 0) / nisoloSubmissions.totalReceived * 100).toFixed(1)}%` : '0%',
    live: (nisolo?.liveUnits ?? 0).toLocaleString('en-US')
  } : {
    received: '4,758',
    receivedDetail: 'Across 320 products',
    sold: '4,010',
    sellThrough: '84.3%',
    live: '667'
  };
  const filteredUnits = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sourceRows.filter(unit => {
      const matchesQuery = !normalizedQuery || `${unit.name} ${unit.size}`.toLowerCase().includes(normalizedQuery);
      const matchesStatus = statusFilter === 'All' || unit.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [sourceRows, query, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredUnits.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleRows = filteredUnits.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const setFilter = (filter: UnitStatus | 'All') => {
    setStatusFilter(filter);
    setPage(1);
  };
  return <div className={embedded ? '' : 'mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14'} aria-labelledby="units-heading" data-id="element-2515" data-mp="1mthijr77w8vp">
      {!embedded && <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-2516" data-mp="2ejc9ttemy8n0">
          <div data-id="element-2517" data-mp="2jiyn22ugrovt">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-2518" data-mp="2jhdugirpmg0l">
              <BoxIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2519" data-mp="25qs7oiz4xlxf" />
              Units
            </span>
            <h1 id="units-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-2520" data-mp="29qt057j2jxnh">Individual units</h1>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-2521" data-mp="328ohupy88anq">Track individual units from receipt through sale, including condition, price, and the channel where each item sold.</p>
          </div>
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-2522" data-mp="lvmnh67dl0ov">
            <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-2523" data-mp="10izcx60azqkj" />
            {selectedOwner || 'All brands'}
          </span>
        </section>}

      {hasData ? <>
          <section className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-4 ${embedded ? '' : 'mt-8'}`} aria-label="Units summary" data-id="element-2524" data-mp="7vlojwao83c1">
            <UnitMetric label="Units uploaded" value={summary.received} detail={summary.receivedDetail} data-id="element-2525" data-mp="2yd7jz19e5cd" />
            <UnitMetric label="Units sold" value={summary.sold} detail="All recovery channels" data-id="element-2526" data-mp="cu6ff5419ant" />
            <UnitMetric label="Sell-through" value={summary.sellThrough} detail="Including replenishment cycles" data-id="element-2527" data-mp="cwj37ywh2f8s" />
            <UnitMetric label="Live units" value={summary.live} detail="Currently available" data-id="element-2528" data-mp="1o9llcye7zcv2" />
          </section>

          <section className="mt-5 overflow-hidden rounded-3xl border border-ink/10 bg-paper shadow-sm" aria-labelledby="unit-table-heading" data-id="element-2529" data-mp="3hstz6io9jy2o">
            <div className="flex flex-col gap-3 border-b border-ink/10 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-2530" data-mp="3mrezsj7fgjrn">
              <div data-id="element-2531" data-mp="batz5rfq1rj">
                <h2 id="unit-table-heading" className="text-lg font-semibold" data-id="element-2532" data-mp="bu96eppac1v">Unit ledger</h2>
                <p className="mt-1 text-sm text-ink/55" data-id="element-2533" data-mp="5bfb8y0630it">Select a row to inspect its recovery history.</p>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-2534" data-mp="9axt1kwoyvhf">
                {(['All', 'Active', 'Sold', 'Inactive'] as const).map(filter => <button key={filter} type="button" onClick={() => setFilter(filter)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${statusFilter === filter ? 'bg-[#BB5E40] text-cream' : 'bg-cream text-ink/60 hover:bg-ink/5'}`} data-id="element-2535" data-mp="e9it9kby1f5v">{filter}</button>)}
              </div>
            </div>
            <div className="border-b border-ink/10 px-5 py-4 sm:px-6" data-id="element-2536" data-mp="2diit2uto5cit">
              <label className="sr-only" htmlFor="unit-search" data-id="element-2537" data-mp="28rkbzt0uwaei">Search units</label>
              <div className="relative" data-id="element-2538" data-mp="28phdekln18y6">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-2539" data-mp="23ro61o8lm8wb" />
                <input id="unit-search" type="search" value={query} onChange={event => {
              setQuery(event.target.value);
              setPage(1);
            }} placeholder="Search by product name or size" className="w-full rounded-xl border border-ink/10 bg-cream px-10 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40]/40 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-2540" data-mp="1yv7gqqpnxohn" />
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-2541" data-mp="2hlyqvuhh5ix3">
              <table className="w-full min-w-[1000px] text-left text-xs" data-id="element-2542" data-mp="1b421acnmwy7v">
                <thead className="bg-cream/50 text-ink/50" data-id="element-2543" data-mp="164yx0ufhaw0y">
                  <tr data-id="element-2544" data-mp="163e2v5x2tf3m">
                    <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-2545" data-mp="z0upoeumpgv7">Status</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-2546" data-mp="yyi9o78flgks">Product</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-2547" data-mp="u3l2z6ymjjb2">Condition</th>
                    <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-2548" data-mp="18s8u911ft6v7">Received</th>
                    <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-2549" data-mp="13yd1qe3y0c7f">Sold</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-2550" data-mp="1hq1aq8iwakk6">Sell-through</th>
                    <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-2551" data-mp="1dopkq1w3zxbg">Sold on</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink/10" data-id="element-2552" data-mp="1rnfryacn7ddj">
                  {visibleRows.map(unit => <UnitTableRow key={unit.id} unit={unit} onSelect={() => setSelectedUnit(unit)} data-id="element-2553" data-mp="1mr8kgd4lltth" />)}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-3 border-t border-ink/10 px-5 py-3 text-xs text-ink/55 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-2554" data-mp="3oilo6yutxvny">
              <span data-id="element-2555" data-mp="24241xtu5bbh2">Showing {filteredUnits.length ? `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filteredUnits.length)}` : '0'} of {filteredUnits.length} units</span>
              <div className="flex items-center gap-2 self-end sm:self-auto" data-id="element-2556" data-mp="1u7vkq2akf4y7">
                <button type="button" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={currentPage === 1} className="rounded-md p-1 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" aria-label="Previous units page" data-id="element-2557" data-mp="1z7gjabltpbae"><ChevronLeftIcon className="h-4 w-4" data-id="element-2558" data-mp="1bb3xxivte7pe" /></button>
                <span data-id="element-2559" data-mp="1bcx3svylsnwc"><strong className="font-semibold text-ink" data-id="element-2560" data-mp="1bcx30tqs4jva">{currentPage}</strong> of {pageCount}</span>
                <button type="button" onClick={() => setPage(current => Math.min(pageCount, current + 1))} disabled={currentPage === pageCount} className="rounded-md p-1 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" aria-label="Next units page" data-id="element-2561" data-mp="1gc9phddqnoxc"><ChevronRightIcon className="h-4 w-4" data-id="element-2562" data-mp="397b0227mkbqi" /></button>
              </div>
            </div>
          </section>

          {isNisolo && <section className="mt-5" aria-label="Product inventory" data-id="element-2563" data-mp="346n18ud5mqlq">
              <NisoloProductTable products={nisolo?.products ?? []} loading={nisoloLoading} error={nisoloError} data-id="element-2564" data-mp="2z9msgbox79qx" />
            </section>}
        </> : <section className="mt-8 rounded-3xl border border-dashed border-ink/15 bg-paper px-6 py-16 text-center" data-id="element-2565" data-mp="3ry254rkkxfz3">
          <BoxIcon className="mx-auto h-6 w-6 text-[#BB5E40]" aria-hidden="true" data-id="element-2566" data-mp="3my614sbvtojj" />
          <h2 className="mt-4 text-lg font-semibold" data-id="element-2567" data-mp="3i77i3kzieccl">Select an owner to view units</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/55" data-id="element-2568" data-mp="3e645r8go6yg4">Unit-level inventory will appear here with its current state, recovery progress, and channel attribution.</p>
        </section>}

      <AnimatePresence data-id="element-2569" data-mp="9re0f2tfyuty">
        {selectedUnit && <UnitDetailDrawer unit={selectedUnit} onClose={() => setSelectedUnit(null)} data-id="element-2570" data-mp="9qcmmql1ju8j" />}
      </AnimatePresence>
    </div>;
}
function UnitMetric({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-2571" data-mp="1xfld998moa45">
    <BoxIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2572" data-mp="21e0xx6h8l6ee" />
    <p className="mt-4 text-sm text-ink/55" data-id="element-2573" data-mp="26fhxmynzimte">{label}</p>
    <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-2574" data-mp="26c2jby24xht1">{value}</p>
    <p className="mt-1 text-xs text-ink/50" data-id="element-2575" data-mp="2bbp54vw146dg">{detail}</p>
  </article>;
}
function UnitTableRow({
  unit,
  onSelect
}: {
  unit: UnitRow;
  onSelect: () => void;
}) {
  const sellThrough = Math.min(100, Math.round(unit.sold / unit.received * 100));
  const statusStyle = unit.status === 'Sold' ? 'bg-[#DCE5FA] text-[#40569E]' : unit.status === 'Inactive' ? 'bg-ink/10 text-ink/55' : 'bg-forest text-cream';
  const conditionStyle = unit.condition === 'Rescue' ? 'bg-[#A77ACA] text-cream' : unit.condition === 'Resale' ? 'bg-[#4D9CC7] text-cream' : 'bg-[#C97968] text-cream';
  const progressStyle = sellThrough === 100 ? 'bg-forest' : sellThrough > 0 ? 'bg-[#4D9CC7]' : 'bg-ink/15';
  return <tr onClick={onSelect} className="cursor-pointer transition-colors hover:bg-[#BB5E40]/[0.035] focus-within:bg-[#BB5E40]/[0.035]" data-id="element-2576" data-mp="2gh0qg1qph6rk">
    <td className="px-5 py-2.5 sm:px-6" data-id="element-2577" data-mp="2q73serft1v21"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyle}`} data-id="element-2578" data-mp="2q855t2kamtmx">{unit.status}</span></td>
    <th scope="row" className="px-3 py-2.5 text-left font-medium" data-id="element-2579" data-mp="22i1dhrck2gb8"><span className="flex items-center gap-2.5" data-id="element-2580" data-mp="1xiy6w2gxg215"><img src={unit.imageUrl} alt="" className="h-9 w-9 rounded-md object-cover" data-id="element-2581" data-mp="1xjznemgppgq9" /><span data-id="element-2582" data-mp="2cgqjalr6gab4"><span className="block text-ink" data-id="element-2583" data-mp="2cggvn4tluf8l">{unit.name}</span><span className="mt-0.5 block font-normal text-ink/50" data-id="element-2584" data-mp="27gm552dqrro2">{unit.size}</span></span></span></th>
    <td className="px-3 py-2.5" data-id="element-2585" data-mp="3nuue1u5la2h8"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${conditionStyle}`} data-id="element-2586" data-mp="3nuci6ymu9q9u">{unit.condition}</span></td>
    <td className="px-3 py-2.5 text-right tabular-nums text-ink/70" data-id="element-2587" data-mp="3iuznxstn1gx8">{unit.received}</td>
    <td className="px-3 py-2.5 text-right tabular-nums text-ink/70" data-id="element-2588" data-mp="3qe8uzfeo93j4">{unit.sold}</td>
    <td className="px-3 py-2.5" data-id="element-2589" data-mp="3ven7par7x1nh"><div className="flex min-w-32 items-center gap-2" data-id="element-2590" data-mp="3vf6hg46psjqf"><div className="h-2 flex-1 overflow-hidden rounded-full bg-ink/10" data-id="element-2591" data-mp="3helxv5sz6jvv"><div className={`h-full rounded-full ${progressStyle}`} style={{
            width: `${sellThrough}%`
          }} data-id="element-2592" data-mp="3hgf26eg48s0p" /></div><span className="w-9 text-right tabular-nums text-ink/55" data-id="element-2593" data-mp="3lgh66u9bpxj5">{sellThrough}%</span></div></td>
    <td className="px-5 py-2.5 sm:px-6" data-id="element-2594" data-mp="d15u3zbt66l3">{unit.channel ? <ChannelBadge channel={unit.channel} data-id="element-2595" data-mp="i009ssehbf0z" /> : <span className="text-ink/40" data-id="element-2596" data-mp="hyz1uwsrf8nh">—</span>}</td>
  </tr>;
}
function ChannelBadge({
  channel
}: {
  channel: Channel;
}) {
  const marks: Record<Channel, string> = {
    Manymoons: 'M',
    Whatnot: 'W',
    Collective: 'S',
    'Circular Shop': 'M'
  };
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-[#BB5E40]/8 py-1 pl-1 pr-2.5 text-xs font-semibold text-[#94452E]" data-id="element-2597" data-mp="vz9r5vef8f6j"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#BB5E40] text-[10px] font-display text-cream" data-id="element-2598" data-mp="10q87guzetd9s">{marks[channel]}</span>{channel}</span>;
}
function UnitDetailDrawer({
  unit,
  onClose
}: {
  unit: UnitRow;
  onClose: () => void;
}) {
  const sellThrough = Math.min(100, Math.round(unit.sold / unit.received * 100));
  return <>
    <motion.button type="button" aria-label="Close unit details" onClick={onClose} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-40 bg-ink/35 backdrop-blur-[1px]" data-id="element-2599" data-mp="11nd9o6iunle9" />
    <motion.aside initial={{
      opacity: 0,
      x: 42
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 42
    }} transition={{
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1]
    }} className="fixed inset-y-0 right-0 z-50 w-full max-w-xl overflow-y-auto border-l border-ink/10 bg-paper shadow-2xl" aria-label={`${unit.name} unit details`} data-id="element-2600" data-mp="mwe6xc4p3r2l">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-ink/10 bg-paper/95 px-5 py-5 backdrop-blur sm:px-7" data-id="element-2601" data-mp="8wuwudsbq5nn"><h2 className="text-xl font-semibold tracking-tight" data-id="element-2602" data-mp="3y1rx9r3rmz3">{unit.name} <span className="text-ink/45" data-id="element-2603" data-mp="3pzqzq3vdla1v">· {unit.size}</span></h2><button type="button" onClick={onClose} className="rounded-lg p-2 text-ink/55 transition-colors hover:bg-cream hover:text-ink" aria-label="Close details" data-id="element-2604" data-mp="3q1286gzrn5zb"><XIcon className="h-5 w-5" data-id="element-2605" data-mp="3sss5bbqsem3" /></button></div>
      <div className="space-y-5 p-5 sm:p-7" data-id="element-2606" data-mp="3pwvrrfjlxzr">
        <div className="flex gap-4 rounded-2xl border border-ink/10 bg-cream/65 p-4" data-id="element-2607" data-mp="8psylnci7jdq"><img src={unit.imageUrl} alt={`${unit.name} product`} className="h-24 w-20 rounded-xl object-cover" data-id="element-2608" data-mp="378q1ji22xijd" /><div data-id="element-2609" data-mp="3c71ei1no895t"><p className="text-sm font-semibold" data-id="element-2610" data-mp="3c71fo4zeqf7e">{unit.name}</p><p className="mt-1 text-sm text-ink/55" data-id="element-2611" data-mp="3c7ucad4njobt">{unit.size} · {unit.condition}</p><p className="mt-3 font-display text-2xl font-semibold" data-id="element-2612" data-mp="3h5vukvu97jm8">${unit.currentPrice.toFixed(2)}</p><p className="text-xs text-ink/50" data-id="element-2613" data-mp="3lyfjuq2b036v">Current recovery price</p></div></div>
        <DetailSection title="Inventory" data-id="element-2614" data-mp="2oii7rqk9s4fu"><DetailMetric label="Received" value={String(unit.received)} data-id="element-2615" data-mp="2oh737oj02sno" /><DetailMetric label="Sold" value={String(unit.sold)} data-id="element-2616" data-mp="2tgjoi4u83ro5" /><DetailMetric label="Current units on hand" value={String(unit.received - unit.sold)} data-id="element-2617" data-mp="2tf8n2bo92w07" /><DetailMetric label="Available for sale" value={String(unit.received - unit.sold)} data-id="element-2618" data-mp="binparezv9yc" /><div className="col-span-2 mt-1" data-id="element-2619" data-mp="7hllrye1obqk"><div className="flex items-center justify-between text-sm" data-id="element-2620" data-mp="7in14f23fke3"><span className="font-medium text-ink/65" data-id="element-2621" data-mp="lhcxttl03i1x">Sell-through</span><span className="font-semibold tabular-nums" data-id="element-2622" data-mp="gh8frz4g2f9l">{sellThrough}%</span></div><div className="mt-2 h-2 overflow-hidden rounded-full bg-ink/10" data-id="element-2623" data-mp="gfxatvz9j1gw"><div className="h-full rounded-full bg-forest" style={{
                width: `${sellThrough}%`
              }} data-id="element-2624" data-mp="ug875ejcd89g" /></div></div></DetailSection>
        <DetailSection title="Sales" data-id="element-2625" data-mp="qewnrqr29j9p"><DetailMetric label="MSRP" value={`$${unit.msrp.toFixed(2)}`} data-id="element-2626" data-mp="qdljzqxm8bil" /><DetailMetric label="Starting price" value={`$${(unit.msrp * 0.5).toFixed(2)}`} data-id="element-2627" data-mp="145hxopyjeivg" /><DetailMetric label="Current price" value={`$${unit.currentPrice.toFixed(2)}`} data-id="element-2628" data-mp="z7gem5142jjz" /><DetailMetric label="Total sold" value={`$${(unit.sold * unit.currentPrice).toFixed(2)}`} data-id="element-2629" data-mp="1e4qx3yc68pp7" /><div className="col-span-2 rounded-xl bg-[#BB5E40]/5 p-3" data-id="element-2630" data-mp="195w3e1co1gqb"><p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2631" data-mp="197h28360jltz">Channel attribution</p>{unit.channel ? <div className="mt-2" data-id="element-2632" data-mp="1spl9qgbld0ov"><ChannelBadge channel={unit.channel} data-id="element-2633" data-mp="1wtaueup5ay4m" /></div> : <p className="mt-2 text-sm text-ink/55" data-id="element-2634" data-mp="1wrzu54uws8i9">Not sold yet—channel will appear after the first sale.</p>}</div></DetailSection>
        <div className="flex items-center gap-2 rounded-xl border border-forest/15 bg-forest/5 p-3 text-sm text-ink/65" data-id="element-2635" data-mp="1ivmc9umgq539"><CheckCircle2Icon className="h-4 w-4 shrink-0 text-forest" aria-hidden="true" data-id="element-2636" data-mp="1nufs3tp8286l" />Live since {unit.dateLive}</div>
      </div>
    </motion.aside>
  </>;
}
function DetailSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-ink/10 bg-paper" data-id="element-2637" data-mp="104bmjgsh3ycy"><h3 className="border-b border-ink/10 px-4 py-3 text-sm font-semibold" data-id="element-2638" data-mp="151vax9ivxrim">{title}</h3><div className="grid grid-cols-2 gap-x-4 gap-y-5 p-4" data-id="element-2639" data-mp="14zqwhxnd1a43">{children}</div></section>;
}
function DetailMetric({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-2640" data-mp="v3fmjeb8swct"><p className="text-xs text-ink/50" data-id="element-2641" data-mp="v35t1gqzo72d">{label}</p><p className="mt-1 text-lg font-semibold tabular-nums text-ink" data-id="element-2642" data-mp="u8ths11xeo52">{value}</p></div>;
}